import _ from 'underscore'
import Backbone from '@polleverywhere/backbone'
import URI from 'urijs'
import { capitalize, generateHashName } from '../util'
import PlayerScore from './player_score.js'

/**
 * Collection of score models fetched from the backend leaderboard API. When no
 * request params are specified, collection is populated by the top 10 scores
 * sorted by rank. If `participant_id` is specified, collection retrieves the
 * single score model of that participant. If `context` is set to true,
 * collection retrieves the participant score model and surrounding scores.
 */
export default class Leaderboard extends Backbone.Collection {
  /**
   * Defines the model class this collection contains.
   * @return {Backbone.Model.PlayerScore}
   */
  get model () { return PlayerScore }

  /**
   * Stub for setting model property. Without it, jasmine throws an error.
   */
  set model (m) {}

  /**
   * Access to the competitions model this leaderboard is associated with.
   * @return {Competition}
   */
  get competition () {
    return this.options.competition
  }

  /**
   * Defines the API location on the backend to sync data with.
   * @return {string}
   */
  get url () {
    if (!this.competition) {
      throw new Error('Competition is required to fetch leaderboard data.')
    }

    return URI(this.competition.instance.url())
      .segment('leaderboard')
      .toString()
  }

  /**
   * Returns whether screen names are enabled for this collection of scores.
   * @return {boolean}
   */
  get screenNamesEnabled () {
    return this.options.screenNamesEnabled
  }

  set screenNamesEnabled (value) {
    this.options.screenNamesEnabled = value
  }

  /**
   * Constructor initializer.
   * @param {Backbone.Model.PlayerScore[]} [models=[]] List of models to add.
   * @param {Object} [options={}] Configurable options.
   * @param {number} [options.participantId=null] Participant ID of the current
   *    user.
   * @param {number} [options.competition=null] Competition model
   * @param {number} [options.contextSize=3] Default padding size of contextual
   *    scores to fetch that are surrounding the participant's score.
   * @param {boolean} [options.screenNamesEnabled=true] Whether this collection
   *    will use screen names hydrated by the backend. If `false`, screen names
   *    are locally generated instead.
   */
  initialize (models = [], options = {}) {
    this.options = _.defaults(options, {
      participantId: null,
      competition: null,
      contextSize: 3,
      screenNamesEnabled: true
    })
  }

  /**
   * Returns the id or key of the competition this collection belongs to. We
   * must choose a unique key that exists for both presenter & participants
   * since this will be used to generate hash screen names.
   * @return {number}
   */
  getCompetitionId () {
    return this.competition && this.competition.get('shared_key')
  }

  /**
   * Returns size of context to fetch surrounding the participant's score.
   * @return {number}
   */
  getContextSize () {
    return this.options.contextSize
  }

  /**
   * Populate this collection with the participant score and adjacent scores
   * surrounding it.
   * @return {Promise} jQuery.Deferred object.
   */
  fetchParticipantContext () {
    return this.fetch({
      data: { context: this.getContextSize() }
    })
  }

  /**
   * Returns the participant ID of the current user.
   * @return {number}
   */
  getParticipantId () {
    return this.options.participantId
  }

  /**
   * Returns the participant's score model of the current user.
   * @return {Backbone.Model.PlayerScore}
   */
  getParticipantScore () {
    return this.get(this.getParticipantId())
  }

  /**
   * Generates a participant screen name by generating a hash using the
   * specified participant ID provided. When ID is omitted, the current user's
   * participant ID is used as default.
   * @param {number} [participantId=getParticipantId()] Participant ID to hash
   *    and generate a screen name for. Default value is the current user.
   * @return {string} Capitalized name.
   */
  generateScreenName (participantId = this.getParticipantId()) {
    const hashKey = String(this.getCompetitionId()) + String(participantId)
    const hashName = generateHashName(hashKey, { wordCount: 3, fun: this.screenNamesEnabled })
    return capitalize(hashName, {
      delimit: ' '
    })
  }
}
